import { ExclamationCircleTwoTone } from '@ant-design/icons';
import { Lock, SquareArrowOutUpRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Typo from '@/components/Typography/Typo';
import FeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/FeedbackBlockTooltip';
import useFeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/useFeedbackBlockTooltip';
import feedbackDetailUtils from '@/features/Feedback/FeedbackDetail/utils/feedbackDetailutils';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import { useFeedbackDetailShowAll } from '@/stores/WorkspaceCacheStore';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { ContentStyle } from '@/styles/ContentStyle';
import type { FeedbackSentiment } from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { Flex, Space } from '../base';
import Tooltip from '../Tooltip/TooltipV2';
import ProfileBox from './ProfileBox';

type Props = {
  message: ChatMessageWithSentence;
  originalHTML?: boolean;
  customer_info?: CustomerInfoT;
  multiChat?: boolean;
  authorColorMap?: { [key: string]: string };
  focused: boolean;
  source_id: string;
  blocks: FeedbackBlockT[];
  focusedBlock?: string;
};

const ChatBox = (props: Props) => {
  const color = useColor();
  const { setShowAll } = useFeedbackDetailShowAll();
  const ref = useRef<HTMLDivElement>(null);
  const { isEditor } = useUserRole();
  const colors = useColor();
  const { secret, author, created_at, text, link } = props.message;
  const [hover, setHover] = useState(false);

  const { tooltipInfo, currentBlockInfo, onMouseMove, onMouseLeave } =
    useFeedbackBlockTooltip({
      blocks: props.blocks,
    });

  const textHTML = useMemo(() => {
    return feedbackDetailUtils
      .setBlockTags({
        text,
        blocks: props.blocks,
        source_id: props.message.source_id,
        focusedBlock: props.focusedBlock,
      })
      .replaceAll('\n', '<br/>');
  }, [props.blocks, props.focusedBlock, props.message.source_id, text]);

  const authorName = author?.name
    ? author?.type === 3 && !isEditor
      ? 'Contact(Anonymous)'
      : author?.name
    : author?.type === 3
      ? props.customer_info
        ? props.customer_info.name
        : 'Contact(Anonymous)'
      : 'Unknown';

  const isUser = !props.multiChat ? author?.type === 3 : true;

  useEffect(() => {
    setTimeout(() => {
      if (props.focused && ref.current) {
        setShowAll(true);
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 100);
  }, [props.focused, setShowAll]);

  return (
    <Style
      ref={ref}
      a="flex-start"
      j="flex-start"
      g="8px"
      isUser={isUser}
      focused={props.focused}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-chat-id={props.source_id}
      sentiment={props.message.sentence?.sentiment || 2}
      primary={props.message.sentence?.primary}
    >
      {isUser && props.message.sentence && (
        <SentiemntIndicator sentiment={props.message.sentence?.sentiment} />
      )}
      <ProfileBox
        author={author}
        color={
          author?.name && props.authorColorMap
            ? props.authorColorMap[author?.name]
            : undefined
        }
      />
      <Flex d="column" style={{ width: 'calc(100% - 48px)' }} g="6px">
        <Flex g={4} style={{ height: 14 }} a="center">
          {props.message.sentence?.primary && (
            <ExclamationCircleTwoTone
              twoToneColor={[
                color.sentiment[props.message.sentence?.sentiment || 2],
                getAlphaColor(
                  color.sentiment[props.message.sentence?.sentiment || 2],
                  0.1,
                ),
              ]}
              style={{ fontSize: 16 }}
            />
          )}
          <Typo type="label2" weight="sb">
            {authorName}
          </Typo>

          <Tooltip
            tooltipNode={getTimezoneDayjs(created_at).format(DATE_FORMAT.chat)}
          >
            <Flex>
              <Typo type="label3" className="text-gray-600">
                {getTimezoneDayjs(created_at).format('h:mm a')}
              </Typo>
            </Flex>
          </Tooltip>

          {secret && (
            <>
              <Space w="1px" />
              <Flex style={{ color: colors.orange200 }} g="2px" a="center">
                <Lock size={12} />
                <Typo type="label3" className="font-medium text-[11px]">
                  shown to agents only
                </Typo>
              </Flex>
            </>
          )}
        </Flex>
        <Flex>
          <ContentStyle
            dangerouslySetInnerHTML={{ __html: textHTML }}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
          />
          <FeedbackBlockTooltip
            tooltipInfo={tooltipInfo}
            currentBlockInfo={currentBlockInfo}
          />
        </Flex>
      </Flex>
      {hover && link && (
        <Flex
          style={{ position: 'absolute', right: 10, top: 8, cursor: 'pointer' }}
        >
          <SquareArrowOutUpRight
            size={16}
            className="text-gray-600"
            onClick={() => window.open(link)}
          />
        </Flex>
      )}
    </Style>
  );
};

type StyleProps = {
  isUser: boolean;
  focused: boolean;
  sentiment: FeedbackSentiment;
  primary?: boolean;
};

const Style = styled(Flex)<StyleProps>`
  position: relative;
  overflow: hidden;
  padding: 10px 8px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, sentiment, primary }) =>
      primary ? theme.colors.sentiment[sentiment] : 'transparent'};
  transition: all 0.2s;
  background-color: ${({ isUser, theme, sentiment }) =>
    isUser
      ? getAlphaColor(theme.colors.sentiment[sentiment], 0.05)
      : theme.colors.NL00};
  animation-delay: 200ms;
  animation: ${({ focused, theme }) =>
      focused &&
      focusAnimation(
        getAlphaColor(theme.colors.primary100, 0.05),
        getAlphaColor(theme.colors.primary100, 1),
      )}
    3s both;
  &:hover {
    background-color: ${({ theme, sentiment }) =>
      getAlphaColor(theme.colors.sentiment[sentiment], 0.1)};
  }
`;

export const focusAnimation = (
  startColor: string,
  endColor: string,
) => keyframes`
  0% {
    border-color: ${startColor};
  }
  100% {
    border-color: ${endColor};
  }
`;

type SentimentIndicatorProps = {
  sentiment?: FeedbackSentiment;
};

const SentiemntIndicator = styled(Flex)<SentimentIndicatorProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: ${(props) =>
    props.theme.colors.sentiment[props.sentiment || 2]};
`;

export default ChatBox;
